import { Grid, Stack, Typography } from '@mui/material';

import { useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { useParams } from 'react-router-dom';

import AccordionComp from 'common/components/Accordion/AccordionComp';
import AIFeedback from 'common/components/AIFeedback/AIFeedback';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import ModalEle from 'common/components/Modal/ModalEle';
import {
	TABLE_CONSTANTS,
	FEEDBACK_OBJECT_TYPE,
	MODULES,
} from 'common/constants';
import { AI_SIMULATION_GENAI_DISCLAIMER } from 'common/constants/AISimulationConstants';
import {
	PROGRESS_BAR_ELEMENTS,
	STATUS_UPDATE_TYPE,
} from 'common/constants/CitationDetails';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import {
	setIsOpenInvententoryCommentDrawer,
	setSummaryTextForReview,
} from 'common/store/reducer/regulationInventoryReducer';

import useTableQuery from 'hooks/useTableQuery';

import {
	fetchSummarayText,
	getBreadCrumbs,
} from 'features/RegulationInventory/RegInventoryUtils';

import { getSummaryAssessmentData } from '../../api';
import {
	IBusinessUnitMappingEntity,
	ICitationDetailsElements,
	IGroupCitationDetails,
} from '../../api/type-def';
import SubCitationSummary from '../../RegulationInventoryDetails/SubCitationSummary/SubCitationSummary';
import SummaryLayout from '../../Summary/SummaryLayout/SummaryLayout';

import CitationDescription from '../CitationDescription/CitationDescription';
import MappingApprovalDailog from '../MappingApprovalDailog/MappingApprovalDailog';
import RegulationBreadcrumbs, {
	ICustomBreadcrumbsProps,
} from '../RegulationBreadcrumb/RegulationBreadcrumbs';
import RegulationDetailsHeader from '../RegulationHeader/RegulationDetailsHeader';
import SummaryApprovalDailog from '../SummaryApprovalDailog/SummaryApprovalDailog';

import styles from './SummaryAndMapping.module.scss';

const {
	SUMMARY_ASSESSMENT,
	SUMMARY_REVIEW,
	MAPPING_ASSESSMENT,
	MAPPING_REVIEW,
	COMPLETE,
} = PROGRESS_BAR_ELEMENTS;

const { DISCLAIMER } = AI_SIMULATION_GENAI_DISCLAIMER;

const { REQUIREMENT_INVENTORY } = MODULES;

const { REGULATORY_SUMMARIZATION } = FEEDBACK_OBJECT_TYPE;

const { APPROVE, REJECT, REVIEW } = STATUS_UPDATE_TYPE;

const { customPageSize } = TABLE_CONSTANTS;

const { OK, BAD_REQUEST, INTERNAL_SERVER_ERROR, CREATED, NOT_FOUND } =
	HTTP_STATUS_CODES;

export interface IRegulationInventorySummaryElements {
	status: string;
	summaryAssessment: IGroupCitationDetails;
	summaryText: string;
	actionType: string;
}

export interface IRegulationInventoryMappingElements {
	taskStatus: string;
	actionType: string;
}

export interface ICommonComponent {
	children?: React.ReactNode;
	shouldEnableSubmitForReviewForMapping?: boolean;
	mappingData?: IBusinessUnitMappingEntity[]; /// For mapping and complete
	submitHandlerForSummary?: (val: IRegulationInventorySummaryElements) => void;
	submitHandlerForMapping?: (val: IRegulationInventoryMappingElements) => void;
	handleSummaryValue?: (data: any) => void;
	storageWrapper: any;
}

const RegInventorySummaryAndMapping = (props: ICommonComponent) => {
	const {
		children,
		submitHandlerForSummary,
		submitHandlerForMapping,
		shouldEnableSubmitForReviewForMapping = false,
		handleSummaryValue,
		storageWrapper,
	} = props;

	const { groupId } = useParams();

	const dispatch = useDispatch();

	const commentDrawer = useSelector(
		(state: RootState) =>
			state.regulationInventory.isOpenInvententoryCommentDrawer,
	);
	const isCommentAdded = useSelector(
		(state: RootState) => state.regulationInventory.isCommentAdded,
	);
	const summaryText = useSelector(
		(state: RootState) => state.regulationInventory.summaryTextForReview,
	);
	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);
	const [isRejected, setIsRejected] = useState<boolean>(false);
	const [isSummaryAvailable, setIsSummaryAvailable] = useState<boolean>(false);

	const [summaryAssessment, setSummaryAssessment] =
		useState<IGroupCitationDetails>({} as IGroupCitationDetails);
	const [showSummaryModal, setShowSummaryModal] = useState<boolean>(false);
	const [showMappingModal, setShowMappingModal] = useState<boolean>(false);
	const [loading, setIsLoading] = useState<boolean>(false);
	const isSummaryRegenerated = useSelector(
		(state: RootState) => state.regulationInventory.regInvAISummaryRegenerated,
	);

	const [selectedAccordians, setSelectedAccordians] = useState<
		ICitationDetailsElements[]
	>([]);

	const { taskStatus } = summaryAssessment;
	const handleAccordianChange = (
		val: ICitationDetailsElements,
		event: React.SyntheticEvent,
	) => {
		event.stopPropagation();
		if (selectedAccordians.includes(val)) {
			setSelectedAccordians((res) =>
				res.filter((accordian) => accordian !== val),
			);
		} else {
			setSelectedAccordians([...selectedAccordians, val]);
		}
	};

	const handleSuccess = async (responseBody: any): Promise<void> => {
		setIsLoading(false);
		setSummaryAssessment(responseBody);
		if (handleSummaryValue) {
			handleSummaryValue(responseBody?.summary);
		}
		if (responseBody.grouping) {
			dispatch(setSummaryTextForReview(responseBody.summary));
		}
	};

	const handleError = (resp: any): void => {
		if (resp?.statusCode === BAD_REQUEST || resp.statusCode === NOT_FOUND) {
			const { errorBody } = resp;
			const responseError: any = resp.error;
			const errorMsg: string =
				(errorBody?.errorMessage as string) ||
				(responseError?.message as string) ||
				'Unknown error';
			dispatch(
				setToastMessage({
					message: errorMsg,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		} else if (resp?.statusCode === INTERNAL_SERVER_ERROR) {
			dispatch(
				setToastMessage({
					message: 'Internal Server Error',
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const handleAPIResponse = (resp: any): void => {
		if (resp?.statusCode === OK || resp?.statusCode === CREATED) {
			handleSuccess(resp?.responseBody);
		} else {
			handleError(resp);
		}
	};

	const getSummaryAssessment = async (id: string): Promise<void> => {
		setIsLoading(true);
		const resp: any = await getSummaryAssessmentData(id);

		handleAPIResponse(resp);
	};

	useEffect(() => {
		getSummaryAssessment(groupId ?? '');
	}, [groupId]);

	const submitForReviewHandlerForSummary = (
		taskStatus: string,
		type: string,
	) => {
		const objSummary: IRegulationInventorySummaryElements = {
			status: taskStatus,
			actionType: type,
			summaryAssessment,
			summaryText: summaryText || '',
		};
		if (submitHandlerForSummary) {
			submitHandlerForSummary(objSummary);
		}
	};
	const handleAccordionText = (rvalue: any, event: any) => {
		handleAccordianChange(rvalue, event);
	};
	const submitForReviewHandlerForMapping = (
		taskStatus: string,
		actionType: string,
	) => {
		const objSummary: IRegulationInventoryMappingElements = {
			taskStatus,
			actionType,
		};
		if (submitHandlerForMapping) {
			submitHandlerForMapping(objSummary);
		}
	};

	const onSummaryMappingReject = () => {
		dispatch(setIsOpenInvententoryCommentDrawer(true));
		setIsRejected(true);
	};

	const isAssignedUserSameAsLoggedInUser =
		loggedInUser?.userId === summaryAssessment?.assignedUserId;

	const { updateTableQuery: updateSummaryRegTableQuery } = useTableQuery({
		id: 'SummaryRegTable',
	});
	const { updateTableQuery: updateMappingRegTableQuery } = useTableQuery({
		id: 'MappingRegTable',
	});

	const onSummaryMappingApproval = () => {
		if (taskStatus === SUMMARY_REVIEW) {
			setShowSummaryModal(true);
			const defaultQuery = {
				pageNumber: 0,
				pageSize: customPageSize,
			};
			updateSummaryRegTableQuery(defaultQuery);
		} else {
			setShowMappingModal(true);
			const defaultQuery = {
				pageNumber: 0,
				pageSize: customPageSize,
			};
			updateMappingRegTableQuery(defaultQuery);
		}
	};

	const handleModalCancelClick = () => {
		setShowSummaryModal(false);
	};

	const handleMappingModalCancelClick = () => {
		setShowMappingModal(false);
	};

	const handleModalSubmitClick = () => {
		setShowSummaryModal(false);
		submitForReviewHandlerForSummary(MAPPING_ASSESSMENT, APPROVE);
	};

	const handleMappingModalSubmitClick = () => {
		setShowMappingModal(false);
		submitForReviewHandlerForMapping(COMPLETE, APPROVE);
	};

	useEffect(() => {
		setIsSummaryAvailable((summaryText && summaryText?.length > 0) || false);

		if (summaryAssessment && isRejected && isCommentAdded && !commentDrawer) {
			if (taskStatus === SUMMARY_REVIEW) {
				submitForReviewHandlerForSummary(SUMMARY_ASSESSMENT, REJECT);
			} else {
				submitForReviewHandlerForMapping(MAPPING_ASSESSMENT, REJECT);
			}
			setIsRejected(false);
		}
	}, [
		summaryAssessment,
		isRejected,
		isCommentAdded,
		commentDrawer,
		summaryText,
	]);

	const checkBusinessUser = (
		mappingData: IBusinessUnitMappingEntity[] = [],
	) => {
		if (mappingData.length) {
			return (
				mappingData.findIndex(
					(ele) =>
						ele.assignedUserId === loggedInUser?.userId &&
						ele.taskStatus === MAPPING_ASSESSMENT,
				) !== -1
			);
		}
		return false;
	};

	const allowMappingSubmitPermission = checkBusinessUser(props?.mappingData);

	const breadCrumbText = useSelector(
		(state: RootState) => state.common.requirementInventoryBreadcrumbText,
	);

	const breadCrumbs: ICustomBreadcrumbsProps[] = getBreadCrumbs(
		breadCrumbText,
		summaryAssessment?.groupName,
	);

	const shouldDisbaleFeedback =
		taskStatus === MAPPING_ASSESSMENT ||
		taskStatus === MAPPING_REVIEW ||
		taskStatus === COMPLETE;

	return (
		<Grid container spacing={2}>
			{loading ? (
				<Loader
					show
					className={styles['progress-bar']}
					loaderText={undefined}
				/>
			) : (
				<>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12} pt={0}>
						<RegulationBreadcrumbs
							items={breadCrumbs}
							storageWrapper={storageWrapper}
						/>
					</Grid>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						{summaryAssessment?.groupId && (
							<RegulationDetailsHeader
								taskId={summaryAssessment.taskId}
								status={taskStatus}
								assignedUserName={summaryAssessment.assignedUserName}
								jurisdiction={summaryAssessment.jurisdiction}
								reviewByDate={summaryAssessment.reviewByDate}
								title={summaryAssessment.groupName}
								mappingData={props?.mappingData}
							/>
						)}
					</Grid>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						<Stack
							className={styles['summary-assessment-details-wrapper']}
							gap="8px"
						>
							<SummaryLayout
								summaryAssessment={summaryAssessment}
								groupId={groupId}
								enable={
									isAssignedUserSameAsLoggedInUser &&
									(taskStatus === SUMMARY_ASSESSMENT ||
										taskStatus === SUMMARY_REVIEW)
								}
								status={taskStatus}
							/>
							{summaryText && (
								<Grid container direction="row" alignItems="center">
									<Typography className={styles['disclaimer-text']}>
										{DISCLAIMER}
									</Typography>
									{isAssignedUserSameAsLoggedInUser && (
										<div className={styles['chat-feedback-grid']}>
											<AIFeedback
												objectType={REGULATORY_SUMMARIZATION}
												module={REQUIREMENT_INVENTORY}
												isDisabled={shouldDisbaleFeedback}
												objectData={{
													prompt: fetchSummarayText(
														summaryAssessment?.grouping || [],
													),
													response: summaryText,
													reference: groupId,
												}}
												shouldResetFeedback={isSummaryRegenerated}
											/>
										</div>
									)}
								</Grid>
							)}
							{children}
							{summaryAssessment?.grouping?.map((res) => (
								<AccordionComp
									expanded={selectedAccordians.includes(res)}
									onChange={(event) => handleAccordianChange(res, event)}
									isLeftAccordian
									key={res.id}
									dataTestId="accordian-comp"
									summary={
										<SubCitationSummary
											checkedCitations={[]}
											selectedAccordians={selectedAccordians}
											res={res}
											isCheckBoxRequired={false}
											enableApplicablityStatus={false}
											isTaskidPresent={summaryAssessment.taskId !== null}
											handleAccordionText={handleAccordionText}
										/>
									}
									details={
										<CitationDescription
											currentNode={res}
											parentNode={res}
											isMultiCheckBoxRequired={false}
										/>
									}
								/>
							))}
						</Stack>
						{taskStatus === SUMMARY_ASSESSMENT &&
							isAssignedUserSameAsLoggedInUser && (
								<Grid
									container
									className={
										styles['summary-assessment-submit-for-review-grid']
									}
									justifyContent="end"
								>
									<ButtonComp
										btnType="primary"
										disabled={!isSummaryAvailable}
										clickHandler={() =>
											submitForReviewHandlerForSummary(SUMMARY_REVIEW, REVIEW)
										}
										data-testid="submit-for-review"
									>
										Submit for Review
									</ButtonComp>
								</Grid>
							)}
						{taskStatus === MAPPING_ASSESSMENT &&
							allowMappingSubmitPermission && (
								<Grid
									container
									className={
										styles['summary-assessment-submit-for-review-grid']
									}
									justifyContent="end"
								>
									<ButtonComp
										btnType="primary"
										disabled={!shouldEnableSubmitForReviewForMapping}
										clickHandler={() =>
											submitForReviewHandlerForMapping(MAPPING_REVIEW, REVIEW)
										}
										data-testid="submit-for-review"
									>
										Submit for Review
									</ButtonComp>
								</Grid>
							)}
						{((taskStatus === SUMMARY_REVIEW &&
							isAssignedUserSameAsLoggedInUser) ||
							(taskStatus === MAPPING_REVIEW &&
								isAssignedUserSameAsLoggedInUser)) && (
							<Grid
								container
								spacing={2}
								justifyContent="flex-end"
								className={styles['summary-assessment-submit-for-review-grid']}
							>
								<Grid item xs={12} sm={4} md={2} lg={2} xl={1}>
									<ButtonComp
										btnType="secondary"
										data-testid="Reject"
										clickHandler={onSummaryMappingReject}
									>
										Reject
									</ButtonComp>
								</Grid>
								<Grid item xs={12} sm={4} md={2} lg={2} xl={1}>
									<ButtonComp
										btnType="primary"
										data-testid="Approve"
										disabled={!isSummaryAvailable}
										clickHandler={onSummaryMappingApproval}
									>
										Approve
									</ButtonComp>
								</Grid>
							</Grid>
						)}
						{taskStatus === SUMMARY_REVIEW && (
							<ModalEle
								open={showSummaryModal}
								sxBoxProps={{ width: '30%', height: '30%', margin: 'auto' }}
							>
								<SummaryApprovalDailog
									cancelClicked={handleModalCancelClick}
									submitClicked={handleModalSubmitClick}
								/>
							</ModalEle>
						)}
						{taskStatus === MAPPING_REVIEW && (
							<ModalEle
								open={showMappingModal}
								sxBoxProps={{ width: '30%', height: '30%', margin: 'auto' }}
							>
								<MappingApprovalDailog
									cancelClicked={handleMappingModalCancelClick}
									submitClicked={handleMappingModalSubmitClick}
								/>
							</ModalEle>
						)}
					</Grid>
				</>
			)}
		</Grid>
	);
};

export default RegInventorySummaryAndMapping;
